import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboardService';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';

import MetricCard from './components/MetricCard';
import EarningsChart from './components/EarningsChart';
import CommissionBreakdown from './components/CommissionBreakdown';
import GoalProgress from './components/GoalProgress';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import AchievementBadges from './components/AchievementBadges';
import ReferralNetworkChart from './components/ReferralNetworkChart';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated, loading: authLoading } = useAuth();
  const [chartType, setChartType] = useState('area');
  const [stats, setStats] = useState(null);
  const [commissionsData, setCommissionsData] = useState([]);
  const [earningsData, setEarningsData] = useState([]);
  const [referralNetworkData, setReferralNetworkData] = useState([]);
  const [activitiesData, setActivitiesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && isAuthenticated && user?.id) {
      loadDashboardData();
    }
  }, [authLoading, isAuthenticated, user?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      const [advisorStats, commissions, earnings, activities] = await Promise.all([
        dashboardService?.getAdvisorStats(user?.id),
        dashboardService?.getCommissionsByLevel(user?.id),
        dashboardService?.getEarningsHistory(user?.id, 6),
        dashboardService?.getActivityFeed(user?.id, 10)
      ]);
      
      setStats(advisorStats);
      setCommissionsData(commissions);
      setActivitiesData(activities);
      
      // Format earnings data for chart
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const formattedEarnings = earnings?.map(item => {
        const date = new Date(item?.month);
        return {
          month: monthNames?.[date?.getMonth()],
          earnings: item?.earnings
        };
      }) || [];
      setEarningsData(formattedEarnings);
      
      // Format referral network data from commissions
      const networkData = commissions?.map(comm => ({
        level: `Nivel ${comm?.level}`,
        count: comm?.referrals
      })) || [];
      setReferralNetworkData(networkData);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const { dashboardService } = await import('../../services/dashboardService');
      const exportData = await dashboardService?.exportDashboardData(user?.id, 'all');
      
      if (!exportData) {
        alert('Error al exportar datos');
        return;
      }
      
      // Convert to CSV format
      const csvContent = [
        ['Tipo', 'Descripción', 'Valor', 'Fecha'],
        ['Métricas', 'Ganancias Mensuales', exportData?.metrics?.totalEarnings || 0, new Date()?.toLocaleDateString()],
        ['Métricas', 'Referidos Activos', exportData?.metrics?.activeReferrals || 0, new Date()?.toLocaleDateString()],
        ['Métricas', 'Puntos Acumulados', exportData?.metrics?.pointsAccumulated || 0, new Date()?.toLocaleDateString()],
        ...(exportData?.activity?.map(act => [
          'Actividad',
          act?.nombre || act?.title,
          act?.stage || '',
          new Date(act?.ultimaActividad || act?.timestamp)?.toLocaleDateString()
        ]) || [])
      ]?.map(row => row?.join(','))?.join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `dashboard_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      link?.click();
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error al exportar datos');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const metricsData = [
    {
      title: "Ganancias del Mes",
      value: stats?.monthlyEarnings?.toFixed(2) || "0.00",
      subtitle: "Enero 2026",
      icon: "DollarSign",
      iconColor: "var(--color-success)",
      trend: "up",
      trendValue: "+15.3%",
      currency: true
    },
    {
      title: "Referidos Activos",
      value: stats?.activeReferrals?.toString() || "0",
      subtitle: "En todos los niveles",
      icon: "Users",
      iconColor: "var(--color-primary)",
      trend: "up",
      trendValue: `+${stats?.totalReferrals || 0} total`,
      currency: false
    },
    {
      title: "Ganancias Totales",
      value: stats?.totalEarnings?.toFixed(2) || "0.00",
      subtitle: "Acumulado",
      icon: "Award",
      iconColor: "#D97706",
      trend: "up",
      trendValue: "Histórico",
      currency: true
    },
    {
      title: "Leads Activos",
      value: stats?.activeLeads?.toString() || "0",
      subtitle: "En seguimiento",
      icon: "UserPlus",
      iconColor: "var(--color-accent)",
      trend: null,
      trendValue: `${stats?.totalLeads || 0} total`,
      currency: false
    }
  ];

  const goalsData = [
    {
      title: "Meta de Ventas Mensual",
      current: 8,
      target: 10,
      unit: "ventas",
      color: "var(--color-primary)"
    },
    {
      title: "Nuevos Referidos",
      current: stats?.totalReferrals || 0,
      target: 15,
      unit: "referidos",
      color: "var(--color-success)"
    },
    {
      title: "Leads Calificados",
      current: stats?.activeLeads || 0,
      target: 25,
      unit: "leads",
      color: "var(--color-accent)"
    }
  ];

  const achievementsData = [
    {
      id: 1,
      title: "Top Performer",
      description: "Entre los 10 mejores asesores del mes",
      icon: "Trophy",
      color: "#FFD700",
      unlocked: true,
      date: "2026-01-01"
    },
    {
      id: 2,
      title: "Red Establecida",
      description: "10+ referidos activos en tu red",
      icon: "Network",
      color: "var(--color-primary)",
      unlocked: stats?.activeReferrals >= 10,
      date: "2025-12-15"
    },
    {
      id: 3,
      title: "Maestro de Ventas",
      description: "50+ ventas completadas",
      icon: "Award",
      color: "var(--color-success)",
      unlocked: false,
      date: null
    },
    {
      id: 4,
      title: "Líder de Equipo",
      description: "20+ referidos directos",
      icon: "Users",
      color: "var(--color-accent)",
      unlocked: false,
      date: null
    }
  ];

  const breadcrumbItems = [
    { label: 'Inicio', path: '/landing-page' },
    { label: 'Dashboard', path: '/user-dashboard' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 mt-16 sm:mt-20">
        <BreadcrumbTrail items={breadcrumbItems} />
        
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Bienvenido, {userProfile?.full_name || 'Asesor'}
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                Panel de control y métricas de rendimiento
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/crm-lead-management')}
                className="flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="UserPlus" size={20} className="mr-2" />
                Gestionar Leads
              </button>
              <button
                onClick={() => navigate('/referral-network')}
                className="flex items-center px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Network" size={20} className="mr-2" />
                Ver Red
              </button>
            </div>
          </div>
        </div>

        {/* Metrics Grid - Mobile optimized */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        {/* Main Content Grid - Mobile stacked, desktop side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
          {/* Left Column - Charts - Full width on mobile */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Earnings Chart */}
            <div className="card p-4 sm:p-5 lg:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
                <div>
                  <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-1">Historial de Ganancias</h2>
                  <p className="text-xs sm:text-sm text-muted-foreground">Últimos 6 meses</p>
                </div>
                <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
                  {['area', 'bar', 'line']?.map((type) => (
                    <button
                      key={type}
                      onClick={() => setChartType(type)}
                      className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-smooth whitespace-nowrap touch-target ${
                        chartType === type
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {type === 'area' ? 'Área' : type === 'bar' ? 'Barras' : 'Línea'}
                    </button>
                  ))}
                </div>
              </div>
              <EarningsChart data={earningsData} chartType={chartType} />
            </div>

            {/* Commission Breakdown */}
            <div className="card p-4 sm:p-5 lg:p-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-4 sm:mb-6">Desglose de Comisiones</h2>
              <CommissionBreakdown commissions={commissionsData} />
            </div>
          </div>

          {/* Right Column - Quick Actions & Activity - Full width on mobile */}
          <div className="space-y-4 sm:space-y-6">
            {/* Quick Actions */}
            <div className="card p-4 sm:p-5 lg:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                <Icon name="Zap" size={20} className="mr-2 text-primary" />
                Acciones Rápidas
              </h2>
              <QuickActions 
                onShareReferral={() => navigate('/referral-network')}
                onImportLead={() => navigate('/crm-lead-management')}
                onRedeemPoints={() => navigate('/rewards')}
              />
            </div>

            {/* Activity Feed */}
            <div className="card p-4 sm:p-5 lg:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                <Icon name="Activity" size={20} className="mr-2 text-primary" />
                Actividad Reciente
              </h2>
              <ActivityFeed activities={activitiesData} />
            </div>
          </div>
        </div>

        {/* Goals Section - Mobile optimized */}
        <div className="card p-4 sm:p-5 lg:p-6 mb-4 sm:mb-6 lg:mb-8">
          <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-4 sm:mb-6">Progreso de Metas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {goalsData?.map((goal, index) => (
              <GoalProgress key={index} {...goal} />
            ))}
          </div>
        </div>

        {/* Bottom Grid - Mobile stacked */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Referral Network Chart */}
          <div className="card p-4 sm:p-5 lg:p-6">
            <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-4 sm:mb-6">Red de Referidos</h2>
            <ReferralNetworkChart data={referralNetworkData} />
          </div>

          {/* Achievement Badges */}
          <div className="card p-4 sm:p-5 lg:p-6">
            <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-4 sm:mb-6">Logros y Reconocimientos</h2>
            <AchievementBadges badges={achievementsData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;